import type { RoomConflictResultDTO } from "@/modules/classes/domain/contracts/sessions";
import { classesApi } from "@/modules/classes/presentation/api/client";
import { useFindAlternativeRooms } from "@/modules/classes/presentation/api/queries";
import { useRoomQuery } from "@/modules/master/presentation/api/queries";
import { activePrivateLessonSharedMembershipLabel } from "@/modules/private-lessons/domain/lib/active-private-lesson-package-display";
import { PrivateLessonGroupPackageBadge } from "@/modules/private-lessons/presentation/ui/PrivateLessonGroupPackageBadge";
import type { ActivePrivateLessonPackageDTO } from "@/modules/students/domain/repo/active-package.dto";
import { studentsApi } from "@/modules/students/presentation/api/client";
import { useCreateStudent } from "@/modules/students/presentation/api/queries";
import { StudentForm } from "@/modules/students/presentation/ui/StudentForm";
import { config } from "@/shared/lib/config";
import {
  addMinutesToTimeHhMm,
  formatDate,
  formatTime,
  toTimeHHmmss,
} from "@/shared/lib/date-time";
import {
  PRIVATE_LESSON_DURATION_SELECT_OPTIONS,
  PRIVATE_LESSON_SLOT_DURATION_SET,
  formatSlotDurationLaoLabel,
  formatSlotHours,
  minutesToSlotHours,
  type PrivateLessonSlotDurationMinutes,
} from "@/shared/lib/private-lesson-slot-duration";
import {
  formatQuarterHhMm,
  isCanonicalQuarterHhMm,
  parseQuarterClock,
} from "@/shared/lib/quarter-time";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  hydrateLookupItems,
} from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormInfiniteMultiCombobox } from "@/shared/ui/FormInfiniteMultiCombobox";
import { QuarterHourMinuteSelect } from "@/shared/ui/QuarterHourMinuteSelect";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardTitle,
  FormDateField,
  FormInput,
  FormRoot,
  FormTextarea,
  Modal,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { differenceInMinutes, format } from "date-fns";
import {
  AlertTriangleIcon,
  Calendar,
  ClockIcon,
  MapPinIcon,
  Timer,
  UserIcon,
  UserPlusIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import type { CreateBookingDTO } from "../../domain/contracts/bookings";
import {
  OFF_SITE_DISTANCE_SELECT_OPTIONS,
  offSiteDistanceFullLabel,
  type OffSiteDistance,
} from "../../domain/lib/off-site-distance";

type StudentLookupItem = { id: string; name: string; memberId: string };

const sessionLocationKindFieldZ = z.enum(["studio", "off_site"]);
const offSiteDistanceFieldZ = z.enum(["near", "far"]);
const sessionEngagementKindFieldZ = z.enum([
  "standard",
  "partner_instructor_only",
]);

const BookingCreateFormSchema = z
  .object({
    studentId: z.string().min(1, "ຕ້ອງເລືອກນັກຮຽນ"),
    packageId: z.string().optional(),
    date: z.string().min(1, "ຕ້ອງເລືອກວັນທີ"),
    startTime: z.string().min(1, "ຕ້ອງເລືອກເວລາເລີ່ມຕົ້ນ"),
    slotDurationMinutes: z.union([
      z.literal(15),
      z.literal(30),
      z.literal(45),
      z.literal(60),
      z.literal(75),
      z.literal(90),
      z.literal(105),
      z.literal(120),
      z.literal(135),
      z.literal(150),
      z.literal(165),
      z.literal(180),
    ]),
    locationKind: sessionLocationKindFieldZ,
    offSiteDistance: offSiteDistanceFieldZ.optional(),
    offSiteLabel: z.string().optional(),
    engagementKind: sessionEngagementKindFieldZ,
    roomId: z.string(),
    instructorIds: z.array(z.string()).min(1, "ຕ້ອງເລືອກຢ່າງນ້ອຍ 1 ຄູ"),
    notes: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!isCanonicalQuarterHhMm(data.startTime)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເວລາເລີ່ມຕົ້ນຕ້ອງເປັນນາທີ 00 / 15 / 30 / 45",
        path: ["startTime"],
      });
    }
    if (
      data.locationKind === "studio" &&
      (!data.roomId || data.roomId.trim().length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງເລືອກຫ້ອງ",
        path: ["roomId"],
      });
    }
    if (
      data.locationKind === "off_site" &&
      data.roomId &&
      data.roomId.trim().length > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ນອກສະຖານທີ່ບໍ່ໃຊ້ຫ້ອງສະຕູດິໂອ — ລ້າງຫ້ອງ",
        path: ["roomId"],
      });
    }
    if (data.locationKind === "studio" && data.offSiteDistance) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ສະຕູດິໂອບໍ່ໃຊ້ໄລຍະນອກສະຖານທີ່",
        path: ["offSiteDistance"],
      });
    }
    if (data.locationKind === "off_site" && !data.offSiteDistance) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງເລືອກໄລຍະ (ໃກ້/ໄກ)",
        path: ["offSiteDistance"],
      });
    }
  });

export type BookingCreateFormValues = z.infer<typeof BookingCreateFormSchema>;

function toTimeHHmm(s: string | undefined): string {
  if (!s) return "09:30";
  if (s.length >= 8) return s.slice(0, 5);
  return s;
}

/** ຈາກຄ່າເລີ່ມ ຫຼືຄ່າເຕັມ ຈົ່ງເປັນ HH:mm ຕາມນທ ທຽນ */
function toQuarterHhMmInitial(s: string | undefined): string {
  const raw = toTimeHHmm(s);
  const { h, m } = parseQuarterClock(raw);
  return formatQuarterHhMm(h, m);
}

/** ຈາກຊ່ວງເລີ່ມ–ຈບ (ເຊັນ calendar) → ນາທີທີ່ກົງກັບທາງເລືອກ ຫຼື 60 ຄ່າເລີ່ມຕົ້ນ */
function inferSlotDurationMinutesFromRange(
  initialStart?: string,
  initialEnd?: string,
): PrivateLessonSlotDurationMinutes {
  if (!initialStart?.trim() || !initialEnd?.trim()) return 60;
  const s = toQuarterHhMmInitial(initialStart);
  const e = toQuarterHhMmInitial(initialEnd);
  try {
    const mins = differenceInMinutes(
      new Date(`2000-01-01T${e}`),
      new Date(`2000-01-01T${s}`),
    );
    if (mins > 0 && PRIVATE_LESSON_SLOT_DURATION_SET.has(mins)) {
      return mins as PrivateLessonSlotDurationMinutes;
    }
  } catch {
    /* ignore */
  }
  return 60;
}

type BookingCreateFormProps = {
  initialStudentId?: string;
  /** Package ທີ່ຈອງ (ຈາກຫນ້າ success ຂາຍແພັກເກດ) */
  initialPackageId?: string;
  /** ວັນທີເລີ່ມຕົ້ນ (ຈາກ calendar slot) */
  initialDate?: string;
  /** ເວລາເລີ່ມຕົ້ນ (ຈາກ calendar slot) */
  initialStartTime?: string;
  /** ເວລາສິ້ນສຸດ (ຈາກ calendar slot) */
  initialEndTime?: string;
  onSubmit: (values: CreateBookingDTO) => void;
  /** ບັນທຶກແລ້ວບໍ່ນຳທາງ ແລະບໍ່ລ້າງຟອມ (ສຳລັບຈອງຕໍ່) */
  onSubmitAndStay?: (values: CreateBookingDTO) => void | Promise<void>;
  submitting?: boolean;
};

export function BookingCreateForm({
  initialStudentId,
  initialPackageId,
  initialDate,
  initialStartTime,
  initialEndTime,
  onSubmit,
  onSubmitAndStay,
  submitting,
}: BookingCreateFormProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const qc = useQueryClient();
  const createStudent = useCreateStudent();

  const methods = RHF.useForm<BookingCreateFormValues>({
    resolver: zodResolver(BookingCreateFormSchema),
    defaultValues: {
      studentId: initialStudentId || "",
      packageId: initialPackageId || "",
      date: initialDate || format(new Date(), "yyyy-MM-dd"),
      startTime: toQuarterHhMmInitial(initialStartTime),
      slotDurationMinutes: inferSlotDurationMinutesFromRange(
        initialStartTime,
        initialEndTime,
      ),
      locationKind: "studio" as const,
      offSiteDistance: undefined,
      offSiteLabel: "",
      engagementKind: "standard" as const,
      roomId: "",
      instructorIds: [] as string[],
      notes: "",
    },
  });

  const { setValue } = methods;
  const studentId = methods.watch("studentId");
  const date = methods.watch("date");
  const startTime = methods.watch("startTime");
  const slotDurationMinutes = methods.watch("slotDurationMinutes");
  const locationKind = methods.watch("locationKind");
  const offSiteDistance = methods.watch("offSiteDistance");
  const roomId = methods.watch("roomId");
  const instructorIds = methods.watch("instructorIds") ?? [];

  useEffect(() => {
    if (locationKind === "off_site" && roomId && roomId.trim().length > 0) {
      setValue("roomId", "");
    }
    if (locationKind === "studio") {
      setValue("offSiteDistance", undefined);
    }
  }, [locationKind, roomId, setValue]);

  const { data: room } = useRoomQuery(
    locationKind === "studio" && roomId ? roomId : "",
  );
  const { data: instructors } = useQuery({
    queryKey: ["master", "instructors", "byIds", instructorIds],
    queryFn: () =>
      hydrateLookupItems<{ id: string; name: string }>(
        `${config.apiUrl}/master/instructors/lookup`,
        instructorIds,
      ),
    enabled: instructorIds.length > 0,
  });

  const [conflictResult, setConflictResult] =
    useState<RoomConflictResultDTO | null>(null);
  const [isCheckingConflict, setIsCheckingConflict] = useState(false);
  const findAlternatives = useFindAlternativeRooms();
  const [showAlternatives, setShowAlternatives] = useState(false);

  const computedEndTime = useMemo(() => {
    if (!startTime?.trim()) return "";
    if (!PRIVATE_LESSON_SLOT_DURATION_SET.has(slotDurationMinutes)) return "";
    return addMinutesToTimeHhMm(startTime, slotDurationMinutes);
  }, [startTime, slotDurationMinutes]);

  // Active packages (FIFO from API)
  const { data: activePackagesData } = useQuery({
    queryKey: ["student", studentId, "active-private-lesson-packages-booking"],
    queryFn: async () => {
      if (!studentId)
        return {
          packages: [] as NonNullable<ActivePrivateLessonPackageDTO>[],
        };
      return studentsApi.listActivePrivateLessonPackages(studentId);
    },
    enabled: !!studentId,
  });

  const activePackages = activePackagesData?.packages ?? [];

  const durationMinutes = useMemo(() => {
    return slotDurationMinutes > 0 ? slotDurationMinutes : 0;
  }, [slotDurationMinutes]);

  const durationLabel = useMemo(
    () => formatSlotDurationLaoLabel(durationMinutes),
    [durationMinutes],
  );

  const hoursNeeded = useMemo(
    () => minutesToSlotHours(durationMinutes),
    [durationMinutes],
  );

  const autoResolvedPackage = useMemo(() => {
    if (initialPackageId) {
      return activePackages.find((pkg) => pkg.id === initialPackageId) ?? null;
    }
    return (
      activePackages.find((pkg) => pkg.remainingHours >= hoursNeeded) ?? null
    );
  }, [activePackages, hoursNeeded, initialPackageId]);

  useEffect(() => {
    if (initialPackageId) {
      setValue("packageId", initialPackageId);
      return;
    }
    setValue("packageId", autoResolvedPackage?.id ?? "");
  }, [initialPackageId, autoResolvedPackage, setValue]);

  const selectedPackage = autoResolvedPackage;
  const lockedOffSite =
    selectedPackage?.templateDefaultLocationKind === "off_site";

  useEffect(() => {
    if (lockedOffSite) {
      setValue("locationKind", "off_site");
      setValue("roomId", "");
      if (!methods.getValues("offSiteDistance")) {
        setValue("offSiteDistance", "near");
      }
    }
  }, [lockedOffSite, setValue, methods]);

  const buildDto = (values: BookingCreateFormValues): CreateBookingDTO => {
    const endHhMm = addMinutesToTimeHhMm(
      values.startTime,
      values.slotDurationMinutes,
    );
    const dto: CreateBookingDTO = {
      studentId: values.studentId,
      date: values.date,
      startTime: toTimeHHmmss(values.startTime),
      endTime: toTimeHHmmss(endHhMm),
      locationKind: values.locationKind,
      engagementKind: values.engagementKind,
      offSiteLabel:
        values.locationKind === "off_site"
          ? values.offSiteLabel?.trim() || null
          : null,
      offSiteDistance:
        values.locationKind === "off_site"
          ? (values.offSiteDistance as OffSiteDistance)
          : null,
      roomId:
        values.locationKind === "studio" ? values.roomId.trim() : undefined,
      instructorIds: values.instructorIds ?? [],
      notes: values.notes || undefined,
    };
    if (values.packageId) {
      dto.packageId = values.packageId;
    }
    return dto;
  };

  const runConflictCheck = async (
    values: BookingCreateFormValues,
  ): Promise<boolean> => {
    const start = toTimeHHmmss(values.startTime);
    const end = toTimeHHmmss(
      addMinutesToTimeHhMm(values.startTime, values.slotDurationMinutes),
    );
    setIsCheckingConflict(true);
    try {
      const result = await classesApi.sessions.checkConflict({
        roomId:
          values.locationKind === "studio" ? values.roomId.trim() : undefined,
        date: values.date,
        startTime: start,
        endTime: end,
        instructorIds: values.instructorIds?.length
          ? values.instructorIds
          : undefined,
        sessionLocationKind: values.locationKind,
      });
      if (result.hasConflict) {
        setConflictResult(result);
        try {
          await findAlternatives.mutateAsync({
            date: values.date,
            startTime: start,
            endTime: end,
            excludeRoomId:
              values.locationKind === "studio" ? values.roomId : undefined,
          });
          setShowAlternatives(true);
        } catch (err) {
          console.error("Failed to find alternatives:", err);
        }
        return false;
      }
      setConflictResult(null);
      setShowAlternatives(false);
      return true;
    } catch (err) {
      console.error("Failed to check conflict:", err);
      return false;
    } finally {
      setIsCheckingConflict(false);
    }
  };

  const handleSubmit = async (values: BookingCreateFormValues) => {
    const ok = await runConflictCheck(values);
    if (!ok) return;
    onSubmit(buildDto(values));
  };

  const handleSubmitAndStay = async (values: BookingCreateFormValues) => {
    const ok = await runConflictCheck(values);
    if (!ok) return;
    await onSubmitAndStay?.(buildDto(values));
  };

  const handleCreateStudent = async (
    values: Parameters<typeof createStudent.mutateAsync>[0],
  ) => {
    try {
      const result = await createStudent.mutateAsync(values);
      await hydrateLookupItem<StudentLookupItem>(
        `${config.apiUrl}/students/lookup`,
        result.id,
      );
      qc.invalidateQueries({ queryKey: ["students", "lookup"] });
      setValue("studentId", result.id, { shouldValidate: true });
      setIsCreateModalOpen(false);
    } catch {
      // Error handling is done by the mutation
    }
  };

  const handleFindAlternatives = async () => {
    if (
      !date ||
      !startTime?.trim() ||
      locationKind !== "studio" ||
      !roomId?.trim() ||
      !PRIVATE_LESSON_SLOT_DURATION_SET.has(slotDurationMinutes)
    ) {
      return;
    }
    const endHhMm = addMinutesToTimeHhMm(startTime, slotDurationMinutes);
    try {
      await findAlternatives.mutateAsync({
        date,
        startTime: toTimeHHmmss(startTime),
        endTime: toTimeHHmmss(endHhMm),
        excludeRoomId: roomId,
      });
      setShowAlternatives(true);
    } catch (err) {
      console.error("Failed to find alternatives:", err);
    }
  };

  const handleSelectAlternativeRoom = (altRoomId: string) => {
    methods.setValue("roomId", altRoomId);
    setShowAlternatives(false);
    setConflictResult(null);
  };

  const roomName = room?.name ?? "";
  const offSiteLabelWatch = methods.watch("offSiteLabel");
  const instructorNamesDisplay = (instructors ?? [])
    .map((i) => i.name)
    .join(", ");

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <FormRoot methods={methods} onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Section: ນັກຮຽນ & Package */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">ນັກຮຽນ & Package</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex w-full flex-col items-end gap-2 md:flex-row">
                  <FormInfiniteCombobox<StudentLookupItem>
                    name="studentId"
                    label="ນັກຮຽນ"
                    requiredMark
                    queryKey={["students", "lookup"]}
                    queryFn={(args) =>
                      fetchLookupForInfinite<StudentLookupItem>(
                        `${config.apiUrl}/students/lookup`,
                        args,
                      )
                    }
                    preloadQueryFn={(id) =>
                      hydrateLookupItem<StudentLookupItem>(
                        `${config.apiUrl}/students/lookup`,
                        id,
                      )
                    }
                    getLabel={(item) => `${item.name} (${item.memberId})`}
                    getValue={(item) => item.id}
                    placeholder="ເລືອກນັກຮຽນ..."
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 shrink-0"
                    onClick={() => setIsCreateModalOpen(true)}
                  >
                    <UserPlusIcon className="mr-2 size-4" />
                    ນັກຮຽນໃໝ່
                  </Button>
                </div>
                {studentId && (
                  <div>
                    {selectedPackage ? (
                      <p className="text-muted-foreground text-sm">
                        ແພັກເກດ (FIFO):{" "}
                        <span className="font-medium text-foreground">
                          {selectedPackage.templateName}
                        </span>
                      </p>
                    ) : (
                      <Alert className="mt-2">
                        <AlertDescription>
                          ນັກຮຽນບໍ່ມີ package ທີ່ໃຊ້ງານໄດ້ພຽງພໍກັບຄວາມຍາວທີ່ເລືອກ
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Package Info */}
            {selectedPackage && (
              <div className="rounded-lg border bg-muted/50 p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Template:</span>
                    <p className="flex flex-wrap items-center gap-2 font-semibold">
                      {selectedPackage.templateName}
                      <PrivateLessonGroupPackageBadge
                        packageKind={selectedPackage.packageKind}
                        groupSize={selectedPackage.groupSize}
                      />
                    </p>
                    {activePrivateLessonSharedMembershipLabel(selectedPackage) && (
                      <p className="mt-1 text-muted-foreground text-xs">
                        {activePrivateLessonSharedMembershipLabel(selectedPackage)}
                      </p>
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground">ເຫຼືອ:</span>
                    <p className="font-semibold">
                      {formatSlotHours(selectedPackage.remainingHours)} ຊົ່ວໂມງ
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">ໝົດອາຍຸ:</span>
                    <p className="font-semibold">
                      {formatDate(selectedPackage.expireDate)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Section: ວັນທີ & ເວລາ */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">ວັນທີ & ເວລາ</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <FormDateField name="date" label="ວັນທີ" requiredMark />
                <div className="space-y-2">
                  <div className="flex items-center gap-1 font-medium text-sm leading-none">
                    <span>ເວລາເລີ່ມຕົ້ນ</span>
                    <span aria-hidden className="text-red-500">
                      *
                    </span>
                  </div>
                  <RHF.Controller
                    name="startTime"
                    control={methods.control}
                    render={({ field, fieldState }) => (
                      <>
                        <QuarterHourMinuteSelect
                          value={field.value ?? ""}
                          onChange={field.onChange}
                        />
                        {fieldState.error?.message ? (
                          <p className="text-red-500 text-xs">
                            {fieldState.error.message}
                          </p>
                        ) : null}
                      </>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-1 font-medium text-sm leading-none">
                    <span>ຄວາມຍາວ (ນາທີ)</span>
                    <span aria-hidden className="text-red-500">
                      *
                    </span>
                  </div>
                  <RHF.Controller
                    name="slotDurationMinutes"
                    control={methods.control}
                    render={({ field, fieldState }) => {
                      const raw = Number(field.value);
                      const safe = PRIVATE_LESSON_SLOT_DURATION_SET.has(raw)
                        ? raw
                        : 60;
                      return (
                        <>
                          <SimpleSelect
                            value={String(safe)}
                            onValueChange={(val) => {
                              const m = Number(val);
                              if (!PRIVATE_LESSON_SLOT_DURATION_SET.has(m))
                                return;
                              field.onChange(m);
                            }}
                            options={PRIVATE_LESSON_DURATION_SELECT_OPTIONS}
                            className="h-9 w-full"
                            placeholder="ເລືອກຄວາມຍາວ"
                          />
                          {fieldState.error?.message ? (
                            <p className="text-red-500 text-xs">
                              {fieldState.error.message}
                            </p>
                          ) : null}
                        </>
                      );
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <div className="font-medium text-muted-foreground text-sm leading-none">
                    ເວລາສິ້ນສຸດ <span className="text-xs">(ຄຳນວນອັດຕະໂນມັດ)</span>
                  </div>
                  <div className="flex h-9 w-full items-center rounded-md border border-input border-dashed bg-muted/50 px-3 text-muted-foreground text-sm">
                    {computedEndTime || "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* Duration Display */}
            {durationMinutes > 0 && (
              <div className="rounded-lg border bg-blue-50 p-3 dark:bg-blue-950/20">
                <p className="text-blue-900 text-sm dark:text-blue-200">
                  ລາຍການ: {durationMinutes} ນາທີ (
                  {formatSlotHours(minutesToSlotHours(durationMinutes))} ຊົ່ວໂມງ)
                </p>
                {selectedPackage &&
                  minutesToSlotHours(durationMinutes) >
                  selectedPackage.remainingHours && (
                    <Alert className="mt-2">
                      <AlertDescription className="text-red-600">
                        ຊົ່ວໂມງທີ່ຈອງ (
                        {formatSlotHours(minutesToSlotHours(durationMinutes))})
                        ເກີນຈຳນວນທີເຫຼືອ (
                        {formatSlotHours(selectedPackage.remainingHours)})
                      </AlertDescription>
                    </Alert>
                  )}
              </div>
            )}

            {/* Section: ສະຖານທີ່ ແລະ ປະເພດໂຄງການ */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">ສະຖານທີ່ ແລະ ປະເພດໂຄງການ</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {lockedOffSite ? (
                  <div className="space-y-2 md:col-span-2">
                    <div className="flex items-center gap-1 font-medium text-sm leading-none">
                      <span>ປະເພດສະຖານທີ່</span>
                    </div>
                    <div className="flex items-start gap-3 rounded-lg border bg-muted/40 p-3">
                      <MapPinIcon className="mt-0.5 size-5 shrink-0 text-muted-foreground stroke-[1.5]" />
                      <div className="min-w-0 space-y-1">
                        <Badge variant="outline">ນອກສະຖານທີ່</Badge>
                        <p className="text-muted-foreground text-xs leading-snug">
                          ຕາມແມ່ແບບແພັກເກດ — ຈອງເປັນເວລາຄູ ບໍ່ໃຊ້ຫ້ອງ
                        </p>
                      </div>
                    </div>
                    <RHF.Controller
                      name="offSiteDistance"
                      control={methods.control}
                      render={({ field, fieldState }) => (
                        <div className="space-y-2">
                          <div className="flex items-center gap-1 font-medium text-sm leading-none">
                            <span>ໄລຍະນອກສະຖານທີ່</span>
                            <span aria-hidden className="text-red-500">
                              *
                            </span>
                          </div>
                          <SimpleSelect
                            value={field.value ?? ""}
                            onValueChange={(v) =>
                              field.onChange(v as OffSiteDistance)
                            }
                            options={[...OFF_SITE_DISTANCE_SELECT_OPTIONS]}
                            className="h-9 w-full"
                            placeholder="ເລືອກໃກ້/ໄກ"
                          />
                          {fieldState.error?.message ? (
                            <p className="text-red-500 text-xs">
                              {fieldState.error.message}
                            </p>
                          ) : null}
                        </div>
                      )}
                    />
                  </div>
                ) : (
                  <RHF.Controller
                    name="locationKind"
                    control={methods.control}
                    render={({ field, fieldState }) => (
                      <div className="space-y-2">
                        <div className="flex items-center gap-1 font-medium text-sm leading-none">
                          <span>ປະເພດສະຖານທີ່</span>
                          <span aria-hidden className="text-red-500">
                            *
                          </span>
                        </div>
                        <SimpleSelect
                          value={field.value}
                          onValueChange={(v) =>
                            field.onChange(v as "studio" | "off_site")
                          }
                          options={[
                            { value: "studio", label: "ສະຕູດິໂອ" },
                            { value: "off_site", label: "ນອກສະຖານທີ່" },
                          ]}
                          className="h-9 w-full"
                        />
                        {fieldState.error?.message ? (
                          <p className="text-red-500 text-xs">
                            {fieldState.error.message}
                          </p>
                        ) : null}
                      </div>
                    )}
                  />
                )}
                {locationKind === "off_site" ? (
                  <>
                    <RHF.Controller
                      name="offSiteDistance"
                      control={methods.control}
                      render={({ field, fieldState }) => (
                        <div className="space-y-2">
                          <div className="flex items-center gap-1 font-medium text-sm leading-none">
                            <span>ໄລຍະນອກສະຖານທີ່</span>
                            <span aria-hidden className="text-red-500">
                              *
                            </span>
                          </div>
                          <SimpleSelect
                            value={field.value ?? ""}
                            onValueChange={(v) =>
                              field.onChange(v as OffSiteDistance)
                            }
                            options={[...OFF_SITE_DISTANCE_SELECT_OPTIONS]}
                            className="h-9 w-full"
                            placeholder="ເລືອກໃກ້/ໄກ"
                          />
                          {fieldState.error?.message ? (
                            <p className="text-red-500 text-xs">
                              {fieldState.error.message}
                            </p>
                          ) : null}
                        </div>
                      )}
                    />
                    <FormInput
                      name="offSiteLabel"
                      label="ຊື່ສະຖານທີ່ (ທາງເລືອກ)"
                      placeholder="ຕົວຢ່າງ: ໂຮງຮຽນ ມປລ — ຫ້ອງຟິດເນສ"
                    />
                  </>
                ) : (
                  <div className="hidden md:block" aria-hidden />
                )}
                <RHF.Controller
                  name="engagementKind"
                  control={methods.control}
                  render={({ field }) => (
                    <div className="space-y-2 md:col-span-2">
                      <div className="flex items-center gap-1 font-medium text-sm leading-none">
                        <span>ປະເພດໂຄງການ</span>
                        <span aria-hidden className="text-red-500">
                          *
                        </span>
                      </div>
                      <SimpleSelect
                        value={field.value}
                        onValueChange={(v) =>
                          field.onChange(
                            v as "standard" | "partner_instructor_only",
                          )
                        }
                        options={[
                          { value: "standard", label: "ມາດຕະຖານ" },
                          {
                            value: "partner_instructor_only",
                            label: "Partner (instructor-only)",
                          },
                        ]}
                        className="h-9 w-full"
                      />
                      <p className="text-muted-foreground text-xs">
                        ໂຄງການ Partner: ປິດການເຊັກຊື່ນັກຮຽນ — ໃຊ້ການເຊັກຊື່ຄູຄືເກົ່າ.
                      </p>
                    </div>
                  )}
                />
              </div>
            </div>

            {/* Section: ຫ້ອງ & ຄູຜູ້ສອນ */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">
                {lockedOffSite ? "ຄູຜູ້ສອນ" : "ຫ້ອງ & ຄູຜູ້ສອນ"}
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {locationKind === "studio" ? (
                  <div>
                    <FormInfiniteCombobox<{ id: string; name: string }>
                      name="roomId"
                      label="ຫ້ອງ"
                      requiredMark
                      queryKey={["rooms", "lookup"]}
                      queryFn={(args) =>
                        fetchLookupForInfinite<{ id: string; name: string }>(
                          `${config.apiUrl}/master/rooms/lookup`,
                          args,
                        )
                      }
                      preloadQueryFn={(id) =>
                        hydrateLookupItem<{ id: string; name: string }>(
                          `${config.apiUrl}/master/rooms/lookup`,
                          id,
                        )
                      }
                      getLabel={(item) => item.name}
                      getValue={(item) => item.id}
                      placeholder="ເລືອກຫ້ອງ..."
                    />
                    {room && room.capacity != null && (
                      <p className="mt-1 text-muted-foreground text-xs">
                        ຄວາມຈຸ: {room.capacity} ຄົນສູງສຸດ
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-muted-foreground text-sm md:col-span-1">
                    ບໍ່ໃຊ້ຫ້ອງສະຕູດິໂອສຳລັບລາຍການນອກສະຖານທີ່
                  </div>
                )}
                <FormInfiniteMultiCombobox<{ id: string; name: string }>
                  name="instructorIds"
                  label="ຄູຜູ້ສອນ"
                  requiredMark
                  queryKey={["instructors", "lookup"]}
                  queryFn={(args) =>
                    fetchLookupForInfinite<{ id: string; name: string }>(
                      `${config.apiUrl}/master/instructors/lookup`,
                      args,
                    )
                  }
                  preloadQueryFn={(ids) =>
                    hydrateLookupItems<{ id: string; name: string }>(
                      `${config.apiUrl}/master/instructors/lookup`,
                      ids,
                    )
                  }
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  placeholder="ເລືອກຄູຜູ້ສອນ..."
                />
              </div>
            </div>

            {/* Notes */}
            <FormTextarea
              name="notes"
              label="ໝາຍເຫດ"
              placeholder="ໝາຍເຫດເພີ່ມເຕີມ (ບໍ່ບັງຄັບ)"
              rows={3}
            />

            {/* Actions */}
            <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => methods.reset()}
                disabled={submitting}
              >
                ລ້າງ
              </Button>
              {onSubmitAndStay && (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={submitting || isCheckingConflict}
                  onClick={() => methods.handleSubmit(handleSubmitAndStay)()}
                >
                  {isCheckingConflict
                    ? "ກຳລັງກວດສອບ..."
                    : submitting
                      ? "ກຳລັງບັນທຶກ..."
                      : "ບັນທຶກແລະຈອງຕໍ່"}
                </Button>
              )}
              <Button type="submit" disabled={submitting || isCheckingConflict}>
                {isCheckingConflict
                  ? "ກຳລັງກວດສອບ..."
                  : submitting
                    ? "ກຳລັງບັນທຶກ..."
                    : "ບັນທຶກ"}
              </Button>
            </div>
          </div>

          <Modal
            open={isCreateModalOpen}
            onOpenChange={setIsCreateModalOpen}
            title="ລົງທະບຽນນັກຮຽນໃໝ່"
            size="md"
            className="max-h-[85vh] overflow-y-auto"
          >
            <StudentForm
              onSubmit={handleCreateStudent}
              submitting={createStudent.isPending}
            />
          </Modal>
        </FormRoot>
      </div>

      {/* Right Column: Conflict + Summary */}
      <div className="lg:col-span-1">
        <div className="space-y-6">
          {conflictResult?.hasConflict && (
            <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
              <CardTitle className="flex items-center gap-2 p-4 text-red-900 dark:text-red-100">
                <AlertTriangleIcon className="size-5" />
                ພົບການຂັດແຍ້ງ
              </CardTitle>
              <CardContent className="space-y-4">
                <AlertDescription className="text-red-900 dark:text-red-100">
                  {conflictResult.conflictingSessions.length > 0 &&
                    locationKind === "studio" ? (
                    <>
                      ຫ້ອງທີ່ເລືອກ <strong>{roomName}</strong>{" "}
                      ກຳລັງຖືກໃຊ້ງານໃນເວລາທີ່ຮ້ອງຂໍ.
                    </>
                  ) : null}
                  {conflictResult.busyInstructors &&
                    conflictResult.busyInstructors.length > 0 ? (
                    <>
                      {conflictResult.conflictingSessions.length > 0 && " "}
                      ຄູທີ່ເລືອກບໍ່ວ່າງ:{" "}
                      <strong>
                        {conflictResult.busyInstructors
                          .map((i) => i.name)
                          .join(", ")}
                      </strong>
                    </>
                  ) : null}
                </AlertDescription>
                {conflictResult.busyInstructors &&
                  conflictResult.busyInstructors.length > 0 ? (
                  <div className="space-y-2">
                    <p className="font-semibold text-red-900 text-sm uppercase dark:text-red-100">
                      ຄູທີ່ບໍ່ວ່າງໃນເວລານີ້
                    </p>
                    <ul className="list-inside list-disc space-y-1 text-red-900 text-sm dark:text-red-100">
                      {conflictResult.busyInstructors.map((i) => (
                        <li key={i.id}>{i.name}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {conflictResult.conflictingSessions.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-semibold text-red-900 text-sm uppercase dark:text-red-100">
                      ຊົ່ວໂມງຮຽນທີ່ຊ້ຳກັນ
                    </p>
                    {conflictResult.conflictingSessions.map((session) => (
                      <div
                        key={session.id}
                        className="rounded-lg border border-red-200 bg-white p-3 dark:bg-gray-900"
                      >
                        <p className="font-semibold">{session.templateName}</p>
                        <div className="mt-1 flex items-center gap-2 text-muted-foreground text-xs">
                          <ClockIcon className="size-3" />
                          <span>
                            {formatTime(session.startTime)} -{" "}
                            {formatTime(session.endTime)}
                          </span>
                        </div>
                        {session.instructorNames?.length ? (
                          <div className="mt-1 flex items-center gap-2 text-muted-foreground text-xs">
                            <UserIcon className="size-3" />
                            <span>
                              ຄູຜູ້ສອນ: {session.instructorNames.join(", ")}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
                {locationKind === "studio" && (
                  <Button
                    variant="outline"
                    className="w-full border-red-300 text-red-700 hover:bg-red-100 dark:text-red-300"
                    onClick={handleFindAlternatives}
                    disabled={findAlternatives.isPending || isCheckingConflict}
                  >
                    {findAlternatives.isPending
                      ? "ກຳລັງຄົ້ນຫາ..."
                      : "ແນະນຳຫ້ອງທາງເລືອກ"}
                  </Button>
                )}
                {locationKind === "studio" &&
                  showAlternatives &&
                  findAlternatives.data?.rooms &&
                  findAlternatives.data.rooms.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-semibold text-sm">ຫ້ອງທີ່ມີຢູ່:</p>
                      {findAlternatives.data.rooms.map((altRoom) => (
                        <Button
                          key={altRoom.id}
                          variant="outline"
                          className="w-full justify-start"
                          onClick={() =>
                            handleSelectAlternativeRoom(altRoom.id)
                          }
                        >
                          <MapPinIcon className="mr-2 size-4" />
                          {altRoom.name}
                          {altRoom.capacity != null &&
                            ` (${altRoom.capacity} ຄົນ)`}
                        </Button>
                      ))}
                    </div>
                  )}
                {locationKind === "studio" &&
                  showAlternatives &&
                  findAlternatives.data?.rooms &&
                  findAlternatives.data.rooms.length === 0 && (
                    <Alert>
                      <AlertDescription>
                        ບໍ່ມີຫ້ອງທາງເລືອກທີ່ມີຢູ່ສຳລັບເວລານີ້.
                      </AlertDescription>
                    </Alert>
                  )}
              </CardContent>
            </Card>
          )}

          <Card className="border-border">
            <CardContent className="space-y-4 pt-6">
              <CardTitle className="font-semibold text-base">ສະຫຼຸບ</CardTitle>
              <div className="space-y-3">
                {date && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <Calendar className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ວັນທີ
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">
                        {formatDate(date)}
                      </p>
                    </div>
                  </div>
                )}
                {startTime && computedEndTime && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <ClockIcon className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ເວລາ
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">
                        {formatTime(startTime)} - {formatTime(computedEndTime)}
                      </p>
                    </div>
                  </div>
                )}
                {durationLabel && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <Timer className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ໄລຍະເວລາ
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">
                        {durationLabel}
                      </p>
                    </div>
                  </div>
                )}
                {locationKind === "studio" && roomName && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <MapPinIcon className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ຫ້ອງ
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">{roomName}</p>
                      {room && room.capacity != null && (
                        <p className="mt-1 text-muted-foreground text-xs">
                          ຄວາມຈຸ: {room.capacity} ຄົນ
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {locationKind === "off_site" && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <MapPinIcon className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ສະຖານທີ່
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">
                        {offSiteLabelWatch?.trim() ||
                          offSiteDistanceFullLabel(offSiteDistance) ||
                          "ນອກສະຖານທີ່"}
                      </p>
                      {offSiteDistance ? (
                        <Badge variant="outline" className="mt-1">
                          {offSiteDistanceFullLabel(offSiteDistance)}
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                )}
                {instructorIds.length > 0 && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <UserIcon className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ຄູຜູ້ສອນ
                      </p>
                      <p className="mt-0.5 font-semibold text-sm">
                        {instructorNamesDisplay || "-"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {!date && !startTime && instructorIds.length === 0 && (
                <div className="py-8 text-center">
                  <p className="text-muted-foreground text-sm">
                    ຕື່ມຂໍ້ມູນໃນຟອມເພື່ອເບິ່ງສະຫຼຸບ
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
